<template>
  <VueSelect :="$props" :components="{ Deselect }" :filter="filterFn">
    <template #open-indicator>
      <Icon icon="fe:drop-down" class="vs-open-icon" />
    </template>
  </VueSelect>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { compareTwoStrings } from "string-similarity";
import VueSelect from "vue-select";
import Deselect from "@/components/utils/ListDeselect.vue";

const props = defineProps<{
  noFuzzy?: boolean;
  filter?: (options: string, search: string) => string[];
}>();

function fuzzySearch(options: string[], search: string) {
  const scores: Record<string, number> = {};
  options.forEach((opt) => {
    scores[opt] = compareTwoStrings(
      opt.toLocaleLowerCase(),
      search.toLocaleLowerCase()
    );

    // Fuzzy matching doesn't work well for single-char input (score=0) so
    // add a small term to promote results that contain direct match.
    if (opt.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) {
      scores[opt] += 0.2;
    }
  });

  return options.sort((a, b) => scores[b] - scores[a]);
}

const filterFn = computed(
  () => props.filter ?? (props.noFuzzy ? undefined : fuzzySearch)
);
</script>

<style>
/* Open Indicator */
.vs-open-icon {
  @apply transition-transform duration-300;
}
.vs--open .vs-open-icon {
  @apply rotate-180;
}

/* Vue Select re-styling */

/* Adjust height to avoid flickering */
.v-select.vs--multiple {
  @apply min-h-[2.375rem];
}

.v-select .vs__dropdown-toggle {
  @apply bg-base-100 border-base-content border-opacity-40 rounded-md rounded-b-sm;
  @apply pt-1 pb-0.5 px-1;
  @apply border-t-transparent border-l-transparent border-r-transparent;
  /* @apply hover:bg-base-content/5 focus-within:bg-base-content/5; */
  /* @apply transition-colors duration-150; */
}
.v-select.vs--open .vs__dropdown-toggle {
}

.v-select .vs__dropdown-menu {
  @apply bg-base-100 shadow-none border-base-300 border-opacity-40;
  @apply rounded-lg rounded-t-none p-0 shadow shadow-base-content/10;
  @apply translate-y-[1px];
  @apply min-w-min;
}

.v-select .vs__dropdown-option {
  @apply px-4 py-1 first:pt-[5px] last:pb-[6px];
}
.v-select .vs__dropdown-option--highlight {
  @apply bg-base-content bg-opacity-10 text-base-content transition-colors duration-150 ease-in-out;
}

.v-select .vs__selected {
  @apply m-auto;
}
.vs--multiple .vs__selected {
  @apply badge badge-neutral m-0 mx-[2px] my-[2px];
  @apply py-3;
}
.v-select .vs__selected-options {
  @apply flex items-center;
}

.vs--multiple .vs__deselect {
  @apply fill-base-100/80 ml-0 pl-2;
}

/* Instead of using "remove" button, this will make it overlay whole space */
.select-deselect-full.vs--multiple .vs__deselect {
  @apply absolute top-0 left-0 p-0 w-full h-full opacity-0;
}
.select-deselect-full.vs--multiple .vs__selected {
  @apply relative;
}

.v-select .vs__search {
  @apply border-none;
}
.v-select .vs__open-indicator {
  @apply fill-neutral;
}
.v-select .vs__search::placeholder {
  @apply text-base-content/60 text-sm;
}

.v-select .vs__actions {
  @apply p-0;
}
.v-select .vs__actions > * {
  @apply h-full grow;
}
.v-select .vs__clear {
  @apply m-0;
}
</style>
